import { useEffect, useRef } from "react";
import { motion, useScroll, useTransform, useMotionValue } from "framer-motion";
import { ArrowDown, ArrowUpRight } from "lucide-react";
import { scrollToId } from "@/lib/scroll";

const NodeField = () => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    let raf;
    let w = 0;
    let h = 0;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);

    const resize = () => {
      w = canvas.offsetWidth || window.innerWidth;
      h = canvas.offsetHeight || window.innerHeight;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    window.addEventListener("resize", resize);

    const N = 44;
    const nodes = Array.from({ length: N }, () => ({
      x: Math.random() * (w || 1600),
      y: Math.random() * (h || 900),
      vx: (Math.random() - 0.5) * 0.28,
      vy: (Math.random() - 0.5) * 0.28,
      r: Math.random() * 1.5 + 0.6,
    }));

    const draw = () => {
      ctx.clearRect(0, 0, w, h);
      for (const n of nodes) {
        n.x += n.vx;
        n.y += n.vy;
        if (n.x < 0 || n.x > w) n.vx *= -1;
        if (n.y < 0 || n.y > h) n.vy *= -1;
      }
      for (let i = 0; i < N; i++) {
        for (let j = i + 1; j < N; j++) {
          const a = nodes[i];
          const b = nodes[j];
          const d = Math.hypot(a.x - b.x, a.y - b.y);
          if (d < 140) {
            ctx.strokeStyle = `rgba(45, 212, 191, ${0.09 * (1 - d / 140)})`;
            ctx.lineWidth = 1;
            ctx.beginPath();
            ctx.moveTo(a.x, a.y);
            ctx.lineTo(b.x, b.y);
            ctx.stroke();
          }
        }
      }
      for (const n of nodes) {
        ctx.fillStyle = "rgba(45, 212, 191, 0.45)";
        ctx.beginPath();
        ctx.arc(n.x, n.y, n.r, 0, Math.PI * 2);
        ctx.fill();
      }
      raf = requestAnimationFrame(draw);
    };
    raf = requestAnimationFrame(draw);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
    };
  }, []);

  return <canvas ref={canvasRef} className="absolute inset-0 h-full w-full opacity-70" aria-hidden="true" />;
};

const LINES = [
  { text: "WE BRING", cls: "text-zinc-50" },
  { text: "IDEAS", cls: "text-teal-400" },
  { text: "TO LIFE.", cls: "text-zinc-50" },
];

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yDrift = useTransform(scrollYProgress, [0, 1], [0, 180]);
  const fade = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const tiltX = useTransform(my, [-0.5, 0.5], [7, -7]);
  const tiltY = useTransform(mx, [-0.5, 0.5], [-9, 9]);

  const onMouseMove = (e) => {
    const r = ref.current.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <section
      id="hero"
      data-testid="hero-section"
      ref={ref}
      onMouseMove={onMouseMove}
      className="relative min-h-screen flex items-center overflow-hidden pt-[72px]"
    >
      <NodeField />
      <div className="absolute -top-40 -right-40 w-[560px] h-[560px] rounded-full bg-teal-500/10 blur-[140px] pointer-events-none" />
      <div className="absolute bottom-0 -left-40 w-[480px] h-[480px] rounded-full bg-teal-500/5 blur-[120px] pointer-events-none" />
      <span className="absolute right-[-4%] top-1/2 -translate-y-1/2 font-mono text-[26vw] leading-none text-teal-500/[0.04] select-none pointer-events-none hidden lg:block">
        &lt;/&gt;
      </span>

      <motion.div
        style={{ y: yDrift, opacity: fade }}
        className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full py-24 grid lg:grid-cols-12 gap-16 items-center"
      >
        <div className="lg:col-span-7">
          <div className="overflow-hidden mb-6">
            <motion.p
              initial={{ y: "110%" }}
              animate={{ y: 0 }}
              transition={{ duration: 0.8, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
              className="font-mono text-xs sm:text-sm uppercase tracking-[0.25em] text-teal-400"
              data-testid="hero-eyebrow"
            >
              BTM Tech Developments — Calgary, AB
            </motion.p>
          </div>

          <h1
            data-testid="hero-title"
            className="font-display font-extrabold tracking-tight leading-[1.02] text-5xl sm:text-7xl lg:text-8xl"
          >
            {LINES.map((line, i) => (
              <span key={line.text} className="block overflow-hidden pb-1">
                <motion.span
                  className={`block ${line.cls}`}
                  initial={{ y: "115%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 1, delay: 0.25 + i * 0.14, ease: [0.22, 1, 0.36, 1] }}
                >
                  {line.text}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="mt-8 max-w-xl text-base sm:text-lg text-zinc-400 leading-relaxed"
            data-testid="hero-subtitle"
          >
            A Calgary-based studio crafting mobile apps and websites — from the first sketch of an
            idea to a launched product people love to use.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.05, ease: [0.22, 1, 0.36, 1] }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <button
              data-testid="hero-cta-start-project"
              onClick={() => scrollToId("contact")}
              className="group inline-flex items-center gap-2 bg-teal-500 text-[#121316] font-semibold px-7 py-3.5 rounded-full hover:bg-teal-400 hover:shadow-[0_0_32px_rgba(20,184,166,0.4)] transition-all duration-300"
            >
              Start a project
              <ArrowUpRight size={18} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </button>
            <button
              data-testid="hero-cta-services"
              onClick={() => scrollToId("services")}
              className="inline-flex items-center gap-2 border border-zinc-700 text-zinc-200 font-medium px-7 py-3.5 rounded-full hover:border-teal-500/60 hover:text-teal-300 transition-all duration-300"
            >
              See what we do
              <ArrowDown size={16} />
            </button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.3 }}
            className="mt-14 flex flex-wrap gap-x-10 gap-y-4 font-mono text-xs uppercase tracking-[0.18em] text-zinc-500"
            data-testid="hero-badges"
          >
            <span><span className="text-teal-400">●</span>&nbsp;&nbsp;Mobile Apps</span>
            <span><span className="text-teal-400">●</span>&nbsp;&nbsp;Websites &amp; Web Apps</span>
            <span><span className="text-teal-400">●</span>&nbsp;&nbsp;Idea → Launch</span>
          </motion.div>
        </div>

        <div className="lg:col-span-5 flex justify-center lg:justify-end" style={{ perspective: 1000 }}>
          <motion.div
            initial={{ opacity: 0, scale: 0.92, y: 30 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 1.1, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
            style={{ rotateX: tiltX, rotateY: tiltY, transformStyle: "preserve-3d" }}
            className="relative"
          >
            <div className="absolute -inset-6 rounded-[2rem] bg-teal-500/15 blur-2xl" />
            <div
              data-testid="hero-logo"
              className="relative animate-float-slow bg-white rounded-3xl p-10 sm:p-12 shadow-[0_30px_80px_-20px_rgba(0,0,0,0.7)] ring-1 ring-teal-400/30"
            >
              <img
                src="/btm-logo.png"
                alt="BTM Tech Developments logo"
                className="w-56 sm:w-72 h-auto select-none"
                draggable="false"
              />
            </div>
          </motion.div>
        </div>
      </motion.div>
    </section>
  );
};
